import { Injectable } from "@angular/core";
import {
  Observable,
  BehaviorSubject,
  of,
  timer,
  throwError,
  Subscription,
} from "rxjs";
import { switchMap, catchError, debounceTime } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class DataService {
  private cache = new Map<
    string,
    { data: any; expiry: number; isLockRevaldating: boolean }
  >();
  private cacheDuration = 2 * 60 * 1000; // defualt 5 mintiues
  private refreshPeriod = 1000; // debounce
  private debounceTime = 100; // debounce
  private fetchSubjects = new Map<string, BehaviorSubject<void>>();
  private timerSubscriptions = new Map<string, Subscription>();

  constructor() {}

  getData<T>(cacheKey: string, fetchFunction: () => Promise<T>): Observable<T> {
    const fetchSubject = this.getOrCreateFetchSubject(cacheKey);

    this.scheduleFetch(cacheKey, fetchFunction);

    return fetchSubject.pipe(
      debounceTime(this.debounceTime),
      switchMap(() => {
        const cachedResponse = this.getFromCache<T>(cacheKey);
        if (cachedResponse) {
          return of(cachedResponse);
        } else {
          return this.fetchAndCache(cacheKey, fetchFunction);
        }
      }),
    );
  }

  private getOrCreateFetchSubject(cacheKey: string): BehaviorSubject<void> {
    if (!this.fetchSubjects.has(cacheKey)) {
      const newSubject = new BehaviorSubject<void>(undefined);
      this.fetchSubjects.set(cacheKey, newSubject);
    }
    return this.fetchSubjects.get(cacheKey)!;
  }

  private scheduleFetch<T>(
    cacheKey: string,
    fetchFunction: () => Promise<T>,
  ): void {
    if (this.timerSubscriptions.has(cacheKey)) {
      this.timerSubscriptions.get(cacheKey)!.unsubscribe();
    }

    const timerSubscription = timer(this.refreshPeriod).subscribe(() => {
      console.log("run fetch data");
      const cachedResponse = this.getFromCache<T>(cacheKey);
      if (cachedResponse) {
        this.fetchAndCache(cacheKey, fetchFunction).subscribe();
      }
    });

    this.timerSubscriptions.set(cacheKey, timerSubscription);
  }

  private fetchAndCache<T>(
    cacheKey: string,
    fetchFunction: () => Promise<T>,
  ): Observable<T> {
    return new Observable<T>((observer) => {
      fetchFunction()
        .then((data) => {
          this.setCache(cacheKey, data);
          observer.next(data);
          observer.complete();
          this.getOrCreateFetchSubject(cacheKey).next();
        })
        .catch((error) => {
          observer.error(error);
        });
    }).pipe(
      catchError((error) => {
        return throwError(error);
      }),
    );
  }

  private getFromCache<T>(cacheKey: string): T | null {
    const cached = this.cache.get(cacheKey);
    if (!cached) {
      return null;
    }

    if (Date.now() > cached.expiry) {
      this.cache.delete(cacheKey);
      return null;
    }

    return cached.data;
  }

  private setCache<T>(cacheKey: string, data: T): void {
    const expiry = Date.now() + this.cacheDuration;
    this.cache.set(cacheKey, { data, expiry, isLockRevaldating: false });
  }

  resetFetch(cacheKey: string): void {
    const fetchSubject = this.getOrCreateFetchSubject(cacheKey);
    fetchSubject.next();
  }
}
